'use client';

import { useMemoizedFn } from 'ahooks';
import { useEffect, useRef, useState } from 'react';
import { clawsGetMyClaws, createClawByCode } from '@/app/apis';
import { isClawProvisioning, useClaw, useFetch } from '@/app/hooks';
import { useStore } from '@/app/providers/store';

// 局部轮询：相比全局模块定时器更短的间隔，让开通成功后能尽快接上发送。
const APPLY_POLL_INTERVAL_MS = 1 * 1000;
const APPLY_POLL_TIMEOUT_MS = 5 * 60 * 1000;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const APPLY_READY_ERROR_REASONS = new Set(['ClawAlreadyActive', 'ClawInProgress']);

/**
 * 触发 ViduClaw 开通的统一入口（局部同步轮询版）：
 * - 调 createClawByCode 触发后端开通
 * - 在本 hook 内按固定间隔轮询 GetMyClaws，直到首个 claw 进入非 provisioning 态
 * - 命中后只设置 activeClaw + 终态 clawStatus，并补一次完整刷新（clawPass / clawInfoInitialized）
 *
 * 刻意不在轮询期间把全局 clawStatus 切到 CREATING：
 * 避免 useInitClaw 接管出整页 / 遮罩 "activating" 态，开通过程只在调用方（发送按钮）内联 loading。
 * 若调用方中途卸载，再把 clawStatus 交给全局 provisioning 轮询兜底，避免开通状态丢失。
 * 开通成功后调用方会在同一次点击流程内继续创建 session / 发送消息。
 *
 * Chat 输入框（未开通态拦截发送）使用该入口，便于后续统一处理错误 / 埋点。
 */
export const useApplyForViduClaw = () => {
  const [applying, setApplying] = useState(false);
  const inFlightPromiseRef = useRef<Promise<boolean> | null>(null);
  const cancelledRef = useRef(false);
  const shouldHandoffPollingRef = useRef(false);

  const setClawStatus = useStore((state) => state.setClawStatus);
  const setActiveClaw = useStore((state) => state.setActiveClaw);
  const { getMyClawsAsync } = useClaw();

  const { runAsync: createClawByCodeAsync } = useFetch(createClawByCode, { manual: true });
  const { runAsync: getMyClawsRawAsync } = useFetch(clawsGetMyClaws, { manual: true });

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      if (shouldHandoffPollingRef.current) {
        setClawStatus('CLAW_STATUS_CREATING');
      }
    };
  }, [setClawStatus]);

  const applyForViduClaw = useMemoizedFn(async (): Promise<boolean> => {
    // 同一时刻只允许一次开通流程在飞；重复点击复用同一 promise，避免第二次调用被误判为失败。
    if (inFlightPromiseRef.current) return inFlightPromiseRef.current;

    const run = (async (): Promise<boolean> => {
      cancelledRef.current = false;
      shouldHandoffPollingRef.current = false;
      const pollStartedAt = Date.now();
      setApplying(true);

      try {
        const createRes = await createClawByCodeAsync().catch(() => null);
        // 业务错误中只有「已开通 / 创建中」可继续轮询；其它错误直接失败退出。
        // 请求本身异常（createRes 为 null）时由 useFetch 内部 toast 反馈。
        if (!createRes) return false;
        if (createRes.error && !APPLY_READY_ERROR_REASONS.has(createRes.error.reason ?? '')) return false;

        shouldHandoffPollingRef.current = true;

        // 轮询直到首个 claw 进入 ACTIVE；EXPIRED 是终态但不是开通成功，不能继续发送。
        while (!cancelledRef.current) {
          const { data } = await getMyClawsRawAsync().catch(() => ({ data: null }));
          const first = data?.claws?.[0] ?? null;
          const latestStatus = first?.status ?? null;

          if (first && latestStatus === 'CLAW_STATUS_ACTIVE') {
            // 只落终态：access 据此切到 'ready'，调用方在同一次点击流程里继续真正发送。
            setActiveClaw(first);
            setClawStatus(latestStatus);
            shouldHandoffPollingRef.current = false;
            // 轮询期间只刷了 claws，补一次完整刷新对齐 clawPass / clawInfoInitialized。
            void getMyClawsAsync();
            return true;
          }

          if (first && latestStatus && !isClawProvisioning(latestStatus)) {
            shouldHandoffPollingRef.current = false;
            return false;
          }

          if (Date.now() - pollStartedAt >= APPLY_POLL_TIMEOUT_MS) {
            return false;
          }

          await sleep(APPLY_POLL_INTERVAL_MS);
        }

        return false;
      } finally {
        inFlightPromiseRef.current = null;
        if (!cancelledRef.current) setApplying(false);
      }
    })();

    inFlightPromiseRef.current = run;
    return run;
  });

  return { applyForViduClaw, applying };
};
